import { Alert } from "../../components-v2/Alert";
import { Button } from "../../components-v2/Button";
import { DataTable, RowProps, TableColumn } from "../../components-v2/DataTable";
import { Dialog } from "../../components-v2/Dialog";
import { Box, VBox } from "../../components-v2/Box";
import { observer } from "mobx-react";
import { FC, ReactNode, useEffect } from "react";
import { ArticleSelectorQuestion, NumberField, NumberQuestion } from "../common/commonComponents";
import {
  calculateEditedNumberOfShares,
  DEFAULT_REVERSE_SPLIT_RATIO,
  ReverseSplitShareForm,
  ShareReverseSplitFractionAdjustment,
} from "./reverseSplitShareDomain";
import { MRadioButtonGroup } from "../../components-v2/RadioButton";
import { TextInput } from "../../components-v2/TextInput";
import { CURRENCY_NOK } from "../../utils/currency";
import { useLocaleFormat, useTranslation } from "../../stores/RootStoreContext";
import { NumberFieldModel } from "../../common/commonForms";
import { shareTypeName } from "../../common/share";
import { Icon } from "../../components-v2/Icon";

export const ReverseSplitShareView: FC<{
  form: ReverseSplitShareForm;
}> = observer(({ form }) => {
  const { t } = useTranslation();
  useEffect(() => form.init(), [form]);

  return (
    <VBox gap={"md"}>
      <NumberQuestion
        field={form.ratio}
        onChange={(e) => form.setReverseSplitRatio(e.target.value)}
        question={t("decisions.shareSplits.reverse-split.ratio-label")}
        isLocked={form.isProjectLocked}
      />
      <TextInput
        label={t("decisions.shareSplits.current-nominal")}
        prefix={CURRENCY_NOK}
        value={form.currentNominalValue}
        disabled
      />
      <TextInput
        label={t("decisions.shareSplits.new-nominal")}
        prefix={CURRENCY_NOK}
        value={form.newNominalValue}
        disabled
      />
      {form.invalidReverseSplitIntoOneMessage && (
        <Alert type="warning" message={t(form.invalidReverseSplitIntoOneMessage)} />
      )}
      {form.invalidReverseSplitMessage && (
        <Alert type="warning" message={t(form.invalidReverseSplitMessage)} />
      )}
      {form.invalidFractionMessage && (
        <>
          <Alert type="warning" message={t(form.invalidFractionMessage)} />
          <>
            <div>{t("decisions.shareSplits.reverse-split.how-to-adjust-fractions.title")}</div>
            <MRadioButtonGroup
              name={"fractionAdjustment"}
              model={form.shareReverseSplitFractionAdjustmentModel}
              disabled={form.isProjectLocked}
            />
            {form.shareReverseSplitFractionAdjustmentModel.value ===
              ShareReverseSplitFractionAdjustment.ONE_PERSON && (
              <>
                <div>{t("decisions.shareSplits.reverse-split.adjustmentName")}</div>
                <MRadioButtonGroup
                  name={"shareReverseSplitFractionAdjustmentName"}
                  model={form.fractionAdjustmentNameModel}
                  disabled={form.isProjectLocked}
                />
              </>
            )}
            {form.shareReverseSplitFractionAdjustmentModel.value ===
              ShareReverseSplitFractionAdjustment.HIGHEST_FRACTION &&
              !form.shareReverseSplitAdjustByHighestFractionPossible && (
                <Alert
                  type="warning"
                  message={t(
                    "decisions.shareSplits.reverse-split.how-to-adjust-fractions.helperText-highestFraction",
                  )}
                />
              )}
          </>
          <Box>
            <Button
              label={t("decisions.shareSplits.reverse-split.show-overview")}
              variant={"secondary"}
              fullWidth={true}
              onClick={() => form.reverseSplitShareOverview.setIsOpen(true)}
              disabled={form.isProjectLocked}
            />
            <ShareReverseSplitOverview form={form} />
          </Box>
          {form.shareReverseSplitFractionAdjustmentModel.value ===
            ShareReverseSplitFractionAdjustment.MANUAL_ADJUSTMENT &&
            !!form.reverseSplitShareOverview.summaryChangedData.length && (
              <>
                <div>{t("decisions.shareSplits.reverse-split.adjusted-ratios")}:</div>
                {form.reverseSplitShareOverview.summaryChangedData.map((shareholder) => (
                  <Box key={shareholder.shareholderName}>
                    <div>{shareholder.shareholderName}</div>
                    {shareholder.adjustedShares.map((share) => (
                      <Box
                        key={`${shareholder.shareholderName}-${share.shareTypeName}`}
                        marginLeft={"md"}
                      >
                        <div>
                          {t(shareTypeName(share.shareTypeName))}: {share.fraction} &rarr;{" "}
                          {share.editedNumber}
                        </div>
                      </Box>
                    ))}
                  </Box>
                ))}
              </>
            )}
        </>
      )}
      <ArticleSelectorQuestion
        question={t("decisions.shareSplits.which-paragraph")}
        model={form.shareCapitalParagraph}
        isLocked={form.isProjectLocked}
      />
    </VBox>
  );
});

interface RelocationsRow {
  id: string;
  rowProps?: RowProps;
  title?: ReactNode;
  numberOfShares?: number;
  fraction?: number;
  shareholderId?: number;
  shareTypeId?: number;
  editedNumberOfShares?: NumberFieldModel;
}

const ShareReverseSplitOverview: FC<{ form: ReverseSplitShareForm }> = observer(({ form }) => {
  const { formatNumber } = useLocaleFormat();
  const { t } = useTranslation();

  const tableColumns: TableColumn<RelocationsRow>[] = [
    {
      name: "title",
      label: "",
      align: "left",
      notSorted: true,
      getValue: (s) => s.title,
    },
    {
      name: "numberOfShares",
      label: t("transactions.current-shares"),
      notSorted: true,
      width: "140px",
      getValue: (s) => formatNumber(s.numberOfShares),
    },
    {
      name: "fraction",
      label: t("transactions.new-fraction"),
      notSorted: true,
      width: "140px",
      getValue: function NewFraction({ fraction }) {
        return (
          <Box alignItems="center" justifyContent="end" gap={"md"}>
            {Number(fraction) % 1 > 0 && <Icon name={"warning"} color={"warning"} />}
            <div>{formatNumber(fraction)}</div>
          </Box>
        );
      },
    },
    {
      name: "editedNumberOfShares",
      label: t("transactions.edited-fraction"),
      notSorted: true,
      width: "140px",
      getValue: function FractionCell({
        fraction,
        editedNumberOfShares,
        shareholderId,
        shareTypeId,
      }) {
        if (shareholderId !== undefined && shareTypeId !== undefined) {
          if (
            editedNumberOfShares &&
            fraction &&
            fraction % 1 !== 0 &&
            form.fractionAdjustment === ShareReverseSplitFractionAdjustment.MANUAL_ADJUSTMENT
          ) {
            return <NumberField field={editedNumberOfShares} />;
          } else {
            return formatNumber(editedNumberOfShares?.toNumber());
          }
        } else if (typeof editedNumberOfShares?.toNumber() === "number") {
          return (
            <strong className="text-error">{formatNumber(editedNumberOfShares.toNumber())}</strong>
          );
        }
      },
    },
  ];

  const tableRows: RelocationsRow[] = [
    {
      id: "total",
      title: <strong>{t("shareholders.total")}</strong>,
    },
    ...form.shareTypes.map((share) => ({
      id: `shareType-${share.sharetypeId}`,
      title: t("decisions.shareSplits.reverse-split.total-share", {
        shareName: t(shareTypeName(share.sharetypeCode)),
      }),
      numberOfShares: form.shareSumByType[share.sharetypeCode],
      fraction:
        form.shareSumByType[share.sharetypeCode] /
        (form.parsedRatio ?? DEFAULT_REVERSE_SPLIT_RATIO),
      shareTypeId: share.sharetypeId,
      editedNumberOfShares: new NumberFieldModel({
        value: calculateEditedNumberOfShares(
          form.shareSumByType,
          form.reverseSplitShareOverview.shareholdersWithFraction,
          share,
        ),
        isPositiveOrZero: true,
        required: true,
      }),
    })),
    {
      id: "empty-row",
      rowProps: { style: { borderTop: "1px solid #f0f0f0" } },
    },
    ...form.reverseSplitShareOverview.shareholdersWithFraction.flatMap((shareholder) => [
      {
        id: `shareholder-${shareholder.shareholderId}`,
        title: <strong>{shareholder.shareholderName}</strong>,
      },
      ...shareholder.data
        .filter((d) => d.shareTypeId !== undefined)
        .map((d) => ({
          id: `${shareholder.shareholderId}-${d.shareTypeId}`,
          title: t(shareTypeName(d.sharetypeCode)),
          shareholderId: shareholder.shareholderId,
          shareTypeId: d.shareTypeId,
          numberOfShares: d.numberOfShares,
          fraction: d.fraction,
          editedNumberOfShares: d.editedNumberOfShares,
        })),
    ]),
  ];

  return (
    <>
      {form.reverseSplitShareOverview.isOpen && (
        <Dialog
          title={t("decisions.shareSplits.reverse-split.overview.title")}
          cancelLabel={t("done")}
          onCancel={() => form.reverseSplitShareOverview.setIsOpen(false)}
          maxWidth={"md"}
        >
          <DataTable
            variant="IN_CARD_DENSE"
            tableColumns={tableColumns}
            tableData={tableRows}
            getId={(row) => row.id}
            rowProps={(row) => row.rowProps}
          />
        </Dialog>
      )}
    </>
  );
});
